import { cloneElement, isValidElement, type ReactElement, type ReactNode, type Ref, type MutableRefObject } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { renderIconBySlug } from "@/lib/event-icons";
import { getStatusBadgeConfig } from "@/lib/utils/appointment-status";
import { getStatusBadgeStyle } from "@/lib/utils/clinic-appointment-status";
import { getInitials } from "@/lib/utils/string-utils";
import { getAppointmentDriverLabel, getEffectiveAppointmentDriverType } from "@/lib/utils/appointment-driver";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";
import type { CalendarEvent } from "./CalendarEventTypes";
import { getEventPetsLabel } from "./calendarEventUtils";

function setRef<T>(ref: Ref<T> | undefined, value: T) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  if (ref && "current" in ref) {
    (ref as MutableRefObject<T>).current = value;
  }
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (value: T) => {
    refs.forEach((ref) => setRef(ref, value));
  };
}

function getAppointmentTypeLabel(type: string, t: (key: string) => string) {
  switch (type) {
    case "consulta":
      return t("typeConsulta");
    case "cirurgia":
      return t("typeCirurgia");
    case "banho_tosa":
      return t("typeBanhoTosa");
    case "retorno":
      return t("typeRetorno");
    case "vacina":
      return t("typeVacina");
    case "aplicacao":
      return t("typeAplicacao");
    default:
      return t("typeOutro");
  }
}

function getFinancialStatusLabel(
  status: CalendarEvent["financial_status"] | undefined,
  t: (key: string) => string,
) {
  switch (status) {
    case "pago":
      return t("tooltip.paymentPaid");
    case "pendente":
      return t("tooltip.paymentPending");
    case "nao_cobrado":
      return t("tooltip.paymentNotCharged");
    default:
      return null;
  }
}

export function EventTooltip({
  event,
  statuses,
  children,
  triggerRef,
}: {
  event: CalendarEvent;
  statuses?: AppointmentStatus[];
  children: ReactNode;
  triggerRef?: Ref<HTMLElement>;
}) {
  const { t } = useTranslation("calendar");

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ ref?: Ref<HTMLElement> }>, {
      ref: mergeRefs((children as ReactElement & { ref?: Ref<HTMLElement> }).ref, triggerRef),
    })
    : <div ref={(node) => setRef(triggerRef, node)}>{children}</div>;

  const patientLabel = getEventPetsLabel(event);
  const ownerName = event.owner?.name?.trim() || t("quickStart.unidentified");
  const professionalName = event.vet?.full_name?.trim() || t("unassigned");
  const statusLabel = getStatusBadgeConfig(event.status, statuses).label;
  const statusBadgeStyle = getStatusBadgeStyle(event.status, statuses);
  const typeLabel = getAppointmentTypeLabel(event.type, t);
  const financialStatusLabel = getFinancialStatusLabel(event.financial_status, t);
  const driverTypeLabel = getAppointmentDriverLabel(
    getEffectiveAppointmentDriverType({
      driverType: event.taxi_dog_type,
      isTaxiDog: event.is_taxi_dog,
    }),
    t,
  );
  const notes = event.notes?.trim();
  const startTime = format(event.start, "HH:mm");
  const endTime = format(event.end, "HH:mm");
  const summaryItems = event.summary_items ?? [];

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        sideOffset={18}
        side="top"
        align="center"
        className="max-w-[340px] rounded-2xl border-border/70 bg-popover/95 p-0 text-popover-foreground shadow-xl backdrop-blur-sm"
      >
        <div className="overflow-hidden rounded-2xl">
          <div className="border-b border-border/60 bg-muted/30 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: event.clinic_appointment_type?.color ? `${event.clinic_appointment_type.color}22` : undefined }}
                >
                  {renderIconBySlug(event.clinic_appointment_type?.icon ?? "clipboard-plus", {
                    tone: "current",
                    sizeClass: "h-4 w-4",
                  })}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{patientLabel}</p>
                  <p className="truncate text-xs text-muted-foreground">{ownerName}</p>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold">{startTime}</p>
                <p className="text-[11px] text-muted-foreground">{endTime}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 px-4 py-3">
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="gap-1 rounded-full border-transparent px-2.5 py-0.5 text-[10px] font-medium" style={statusBadgeStyle}>
                <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
                {statusLabel}
              </Badge>
              <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-[10px] font-medium">
                {typeLabel}
              </Badge>
              {financialStatusLabel ? (
                <Badge
                  variant="outline"
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                    event.financial_status === "pago" && "border-emerald-200 bg-emerald-50 text-emerald-700",
                  )}
                >
                  {financialStatusLabel}
                </Badge>
              ) : null}
              {driverTypeLabel ? (
                <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-[10px] font-medium">
                  {driverTypeLabel}
                </Badge>
              ) : null}
            </div>

            {summaryItems.length > 0 ? (
              <div className="rounded-xl bg-muted/35 px-3 py-2">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  {t("tooltip.itemsAndPackages")}
                </p>
                <div className="space-y-1.5">
                  {summaryItems.slice(0, 4).map((item) => (
                    <div key={`${item.kind}-${item.id}`} className="flex items-center gap-2 text-xs">
                      <span className="shrink-0 font-medium text-muted-foreground">
                        {item.kind === "package" ? t("tooltip.packagePill") : `${item.qty}x`}
                      </span>
                      <span className="min-w-0 truncate text-foreground">{item.title}</span>
                    </div>
                  ))}
                  {summaryItems.length > 4 ? (
                    <p className="text-[11px] text-muted-foreground">
                      {t("tooltip.moreItems", { count: summaryItems.length - 4 })}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {notes ? (
              <div className="rounded-xl bg-muted/50 px-3 py-2">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  {t("appointmentForm.notes")}
                </p>
                <p className="line-clamp-3 whitespace-pre-wrap text-xs leading-relaxed">{notes}</p>
              </div>
            ) : null}

            <div className="flex items-center gap-2 border-t border-border/60 pt-3">
              <Avatar className="h-8 w-8">
                {event.vet?.avatar_url ? (
                  <AvatarImage src={event.vet.avatar_url} alt={t("professionalAlt")} />
                ) : null}
                <AvatarFallback className="text-[10px]">
                  {event.vet?.full_name ? getInitials(event.vet.full_name) : "?"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-xs font-medium">{professionalName}</p>
                <p className="truncate text-[11px] text-muted-foreground">{typeLabel}</p>
              </div>
            </div>
          </div>
        </div>
        <TooltipArrow className="fill-popover/95" />
      </TooltipContent>
    </Tooltip>
  );
}
